import { AndroidEvent } from "@/utils/interface";
import { getNavigationBarHeight } from "@/utils/interface";
import { useDebounceFn } from "ahooks";
import { useCallback, useEffect, useRef, useState } from "react";

export interface ViewportHeight {
    height: number;
    keyboardHeight: number;
    isKeyboardOpen: boolean;
    navigationBarHeight: number;
}

interface UseViewportHeightOptions {
    /** 防抖延迟时间（单位：ms），默认 100 */
    debounceDelay?: number;
}

// 获取当前视口高度
const getViewportHeight = (): number => {
    if ( typeof window === "undefined" ) return 0;
    return window.visualViewport?.height ?? window.innerHeight;
};

export const useViewportHeight = (
    options: UseViewportHeightOptions = {},
): ViewportHeight => {
    const initialViewportHeight = useRef( getViewportHeight() );
    const { debounceDelay = 100 } = options;
    const [ navigationBarHeight ] = useState( getNavigationBarHeight() );

    const [ viewportHeight, setViewportHeight ] = useState<ViewportHeight>( () => {
        const initialHeight = getViewportHeight();
        return {
            height: initialHeight,
            keyboardHeight: 0,
            isKeyboardOpen: false,
            navigationBarHeight,
        };
    } );

    const updateHeight = useCallback( () => {
        const currentHeight = getViewportHeight();
        setViewportHeight( ( prev ) => ( {
            ...prev,
            height: currentHeight,
        } ) );
    }, [] );

    const { run: debouncedUpdate } = useDebounceFn( updateHeight, {
        wait: debounceDelay,
    } );

    useEffect( () => {
        if ( typeof window === "undefined" ) return;

        // 初始化
        updateHeight();

        // 监听 Visual Viewport 变化（优先使用）
        if ( window.visualViewport ) {
            window.visualViewport.addEventListener( "resize", debouncedUpdate );
            window.visualViewport.addEventListener( "scroll", debouncedUpdate );

            return () => {
                window.visualViewport?.removeEventListener( "resize", debouncedUpdate );
                window.visualViewport?.removeEventListener( "scroll", debouncedUpdate );
            };
        }

        // 降级方案：监听 window 事件
        window.addEventListener( "resize", debouncedUpdate );
        window.addEventListener( "orientationchange", updateHeight ); // 屏幕旋转不防抖

        return () => {
            window.removeEventListener( "resize", debouncedUpdate );
            window.removeEventListener( "orientationchange", updateHeight );
        };
    }, [ updateHeight, debouncedUpdate ] );

    useEffect( () => {
        const onKeyboardShow = ( keyboardHeight: any ) => {
            const pixelRatioKeyboardHeight = Number( keyboardHeight ) || 0;
            setViewportHeight( ( prev ) => ( {
                ...prev,
                keyboardHeight: pixelRatioKeyboardHeight,
                isKeyboardOpen: true,
                navigationBarHeight: 0, // 键盘弹出时隐藏导航栏高度
            } ) );
        };

        const onKeyboardHide = () => {
            setViewportHeight( ( prev ) => ( {
                ...prev,
                keyboardHeight: 0,
                isKeyboardOpen: false,
                navigationBarHeight: getNavigationBarHeight(), // 键盘消失的时候重置导航栏高度
            } ) );
        };

        AndroidEvent.onKeyboardShow( onKeyboardShow );
        AndroidEvent.onKeyboardHide( onKeyboardHide );

        return () => {
            // 清理键盘事件监听器
            if ( typeof window !== "undefined" ) {
                window.onKeyboardShow = undefined;
                window.onKeyboardHide = undefined;
            }
        };
    }, [] );

    // 统一管理 CSS 变量：当 viewportHeight 状态变化时同步更新
    useEffect( () => {
        if ( typeof document === "undefined" ) return;

        const { height, keyboardHeight, navigationBarHeight } = viewportHeight;
        const currentViewportHeight = getViewportHeight();
        const viewportDelta = initialViewportHeight.current - currentViewportHeight;
        const isSystemResize = viewportDelta > 100;

        // 设置视口高度单位（用于 vh 计算）
        document.documentElement.style.setProperty(
            "--vh",
            `${height * 0.01}px`,
        );

        // 设置键盘偏移量
        const finalKeyboardOffset = isSystemResize
            ? 0
            : Math.max( 0, keyboardHeight );
        document.documentElement.style.setProperty(
            "--keyboard-offset",
            `${finalKeyboardOffset}px`,
        );

        // 设置导航栏高度
        document.documentElement.style.setProperty(
            "--navigationBar",
            `${navigationBarHeight}px`,
        );
    }, [ viewportHeight ] );

    return {
        ...viewportHeight,
        navigationBarHeight,
    };
};
